import type { FC } from 'react'
import { useCallback, useMemo } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, useTheme } from '@mui/material'
import {
  LinkOutlined,
  LockOutlined,
  NotificationsNoneOutlined,
  NotificationsOffOutlined,
  VolumeOffOutlined,
  VolumeUpOutlined,
} from '@mui/icons-material'
import { useSSEConnectionContext } from '@/infra/sse'
import { useEncryptionIndicator } from '@/features/events/shared/hooks'
import {
  AUDIO_LABELS,
  type AudioState,
  CONNECTION_LABELS,
  type ConnectionState,
  CRYPTO_LABELS,
  type CryptoState,
  getAudioColor,
  getConnectionColor,
  getCryptoColor,
  getNotificationColor,
  NOTIFICATION_LABELS,
  type NotificationState,
  StatusIndicator,
  useAudioNotificationsContext,
  useSystemNotifications,
} from '@/features/status'

interface SidebarStatusSectionProps {
  readonly isCollapsed?: boolean
}

export const SidebarStatusSection: FC<SidebarStatusSectionProps> = ({ isCollapsed = false }) => {
  const theme = useTheme()
  const sseContext = useSSEConnectionContext()
  const { state: notificationStatus, requestPermission } = useSystemNotifications()
  const audio = useAudioNotificationsContext()

  const connectionState = useMemo(
    () => deriveConnectionState(sseContext.isConnected, sseContext.isConnecting),
    [sseContext.isConnected, sseContext.isConnecting],
  )

  const notificationState = useMemo(
    () =>
      deriveNotificationState(
        notificationStatus.isEnabled,
        notificationStatus.isRequesting,
        notificationStatus.isSupported,
      ),
    [notificationStatus.isEnabled, notificationStatus.isRequesting, notificationStatus.isSupported],
  )

  const audioState = useMemo(
    () => deriveAudioState(audio.isEnabled, audio.isInitialized),
    [audio.isEnabled, audio.isInitialized],
  )

  const connectionColor = useMemo(
    () => getConnectionColor(connectionState, theme),
    [connectionState, theme],
  )

  const notificationColor = useMemo(
    () => getNotificationColor(notificationState, theme),
    [notificationState, theme],
  )

  const isEncrypted = useEncryptionIndicator()

  const cryptoState: CryptoState = isEncrypted ? 'loaded' : 'missing'

  const audioColor = useMemo(() => getAudioColor(audioState, theme), [audioState, theme])

  const cryptoColor = useMemo(() => getCryptoColor(cryptoState, theme), [cryptoState, theme])

  const isConnectionPulsing = connectionState === 'connecting' || connectionState === 'initializing'
  const isNotificationPulsing = notificationState === 'requesting'
  const isNotificationClickable = notificationState === 'disabled'

  const handleNotificationClick = useCallback(() => {
    if (notificationState === 'disabled') {
      void requestPermission()
    }
  }, [notificationState, requestPermission])

  const handleAudioClick = useCallback(() => {
    audio.toggle()
  }, [audio])

  return (
    <Box sx={containerSx(isCollapsed)}>
      <StatusIndicator
        icon={<LinkOutlined />}
        label={CONNECTION_LABELS[connectionState]}
        statusColor={connectionColor}
        isCollapsed={isCollapsed}
        isPulsing={isConnectionPulsing}
      />
      <StatusIndicator
        icon={<LockOutlined />}
        label={CRYPTO_LABELS[cryptoState]}
        statusColor={cryptoColor}
        isCollapsed={isCollapsed}
      />
      <StatusIndicator
        icon={
          notificationState === 'enabled' ? (
            <NotificationsNoneOutlined />
          ) : (
            <NotificationsOffOutlined />
          )
        }
        label={NOTIFICATION_LABELS[notificationState]}
        statusColor={notificationColor}
        isCollapsed={isCollapsed}
        isPulsing={isNotificationPulsing}
        isClickable={isNotificationClickable}
        {...(isNotificationClickable && { onClick: handleNotificationClick })}
      />
      <StatusIndicator
        icon={audioState === 'ready' ? <VolumeUpOutlined /> : <VolumeOffOutlined />}
        label={AUDIO_LABELS[audioState]}
        statusColor={audioColor}
        isCollapsed={isCollapsed}
        isClickable
        onClick={handleAudioClick}
      />
    </Box>
  )
}

function deriveConnectionState(isConnected: boolean, isConnecting: boolean): ConnectionState {
  if (isConnected) return 'connected'
  if (isConnecting) return 'connecting'
  return 'disconnected'
}

function deriveNotificationState(
  isEnabled: boolean,
  isRequesting: boolean,
  isSupported: boolean,
): NotificationState {
  if (!isSupported) return 'unsupported'
  if (isRequesting) return 'requesting'
  if (isEnabled) return 'enabled'
  return 'disabled'
}

function deriveAudioState(isEnabled: boolean, isInitialized: boolean): AudioState {
  if (!isEnabled) return 'disabled'
  if (!isInitialized) return 'waiting'
  return 'ready'
}

const containerSx = (isCollapsed: boolean): SxProps<Theme> => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: isCollapsed ? 'center' : 'stretch',
  gap: isCollapsed ? 2 : 1.5,
})
